import { memberAsMemberQueryKeys } from "@gymflow/api";
import { ApiType } from "@gymflow/common";
import { formatCurrency, membershipHelper } from "@gymflow/helpers";
import {
  ApiTokenListResponse,
  CurrentSubscriptionDTO,
  MembershipForChangeDTO,
} from "@gymflow/types";
import { infiniteQueryOptions } from "@tanstack/react-query";
import { FormSelectInput } from "apps/portal/src/components/atoms";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { useTranslation } from "react-i18next";

import { ChangeMembershipFormValues } from "../types";

export type FormNewMembershipAsMemberSelectProps = {
  activeMembership: CurrentSubscriptionDTO;
};

export const FormNewMembershipAsMemberSelect = ({
  activeMembership,
}: FormNewMembershipAsMemberSelectProps) => {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { api } = useGymflowModels();

  return (
    <FormSelectInput<ChangeMembershipFormValues, MembershipForChangeDTO>
      name="newMembership"
      label={t("page.hostedPagesProfileChangeMembership.form.newMembership")}
      isMulti={false}
      dataFetchingQuery={() =>
        infiniteQueryOptions({
          queryKey:
            memberAsMemberQueryKeys.subscriptionChangeAvailableMemberships(
              activeMembership.id,
            ),
          queryFn: async ({ pageParam }) =>
            (
              await (
                api as ApiType
              ).profileApi.subscriptionChangeAvailableMemberships({
                subscriptionId: activeMembership.id,
                nextPageToken: pageParam,
                limit: 20,
              })
            ).data,
          select: (data) => {
            const { pageParams, pages } = data;

            const transformedPages = pages.map((page) =>
              page.content
                .filter((e) => e.id !== activeMembership.id)
                ?.map((e) => ({
                  id: e.id,
                  label: e.name,
                  subLabel: `${formatCurrency(
                    e.defaultPrice,
                    club.defaultCurrency,
                  )} ${membershipHelper.getBillingRecurrence({
                    ...e,
                    type: "RECURRING",
                  })}`,
                  value: e,
                })),
            );

            return {
              pageParams,
              pages: transformedPages,
            };
          },
          getNextPageParam: (lastPage: any) =>
            (lastPage as ApiTokenListResponse<MembershipForChangeDTO>)
              .nextPageToken,
          initialPageParam: undefined,
        })
      }
    />
  );
};
